import {
  Component,
  ViewEncapsulation,
  inject,
  HostBinding,
  OnInit,
  OnDestroy,
  AfterViewInit,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { RouterModule, ActivatedRoute } from "@angular/router";
import { TablerIconsModule } from "angular-tabler-icons";
import { MaterialModule } from "src/app/material.module";
import { TranslateModule } from "@ngx-translate/core";
import { ReactiveFormsModule, FormControl } from "@angular/forms";
import { NavService } from "src/app/services/nav.service";
import { SearchService, SearchQuery } from "src/app/services/search.service";
import { BehaviorSubject, Subscription, debounceTime } from "rxjs";
import {
  filterNavItemsForSearch,
  getSearchableNavItems,
} from "src/app/shared/helpers/nav-search.helper";
import {
  ViewContextHistoryStateService,
  eventAction,
} from "src/app/shared/services/view-context-history-state.service";

export interface SearchDialogViewModelState {
  searchQuery: string;
  resultCount: number;
  selectedResult: any | null;
  recentSearches: SearchQuery[];
}

@Component({
  selector: "search-dialog",
  imports: [
    RouterModule,
    MaterialModule,
    TablerIconsModule,
    ReactiveFormsModule,
    TranslateModule,
    CommonModule,
  ],
  templateUrl: "search-dialog.component.html",
  styleUrl: "search-dialog.component.scss",
  encapsulation: ViewEncapsulation.None,
  host: {
    class: "search-dialog-container",
    "[style.display]": "'flex'",
    "[style.flex-direction]": "'column'",
    "[style.height]": "'100%'",
    "[style.overflow]": "'hidden'",
  },
})
export class AppSearchDialogComponent
  implements OnInit, OnDestroy, AfterViewInit {
  searchControl = new FormControl("");
  recentSearches: SearchQuery[] = [];
  private filteredResultsSubject = new BehaviorSubject<any[]>([]);
  filteredResults$ = this.filteredResultsSubject.asObservable();
  private subscriptions: Subscription[] = [];

  private navService = inject(NavService);
  private searchService = inject(SearchService);
  private route = inject(ActivatedRoute);
  private viewContextHistorySvc: ViewContextHistoryStateService<SearchDialogViewModelState> =
    inject(ViewContextHistoryStateService);

  get navItems() {
    return this.navService.navItems();
  }

  get navItemsData() {
    return getSearchableNavItems(this.navItems);
  }

  ngOnInit(): void {
    this.setViewState(
      "route_entered",
      `Search dialog opened with initial query: "${this.searchControl.value || ""}"`,
    );
    // Subscribe to recent searches
    const recentSub = this.searchService.recentSearches$.subscribe(
      (searches) => {
        this.recentSearches = searches;
      },
    );
    this.subscriptions.push(recentSub);

    // Setup debounced search for both filtering and saving to history
    const searchSub = this.searchControl.valueChanges
      .pipe(debounceTime(600))
      .subscribe((query) => {
        const results = this.getFilteredItems(query || "");
        this.filteredResultsSubject.next(results);

        if (query && query.trim() !== "") {
          this.searchService.addSearch(query, results.length);
          this.setViewState(
            "search_performed", `Search features/pages: "${query}" - ${results.length} matches found`,
          );
        }
      });
    this.subscriptions.push(searchSub);
  }

  ngAfterViewInit(): void {
    this.setViewState("modal_ready", "Search dialog initialized");
  }

  private getFilteredItems(searchText: string): any[] {
    return filterNavItemsForSearch(this.navItemsData, searchText);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  selectRecentSearch(query: string): void {
    // Update control value without emitting to avoid debounce
    this.searchControl.setValue(query, { emitEvent: false });
    // Trigger immediate filter by updating the subject directly
    const results = this.getFilteredItems(query);
    this.filteredResultsSubject.next(results);
    // Save to history to move it to top as most recent
    this.searchService.addSearch(query, results.length);
    this.setViewState("search_performed", `Selected recent search: "${query}"`);
  }

  removeRecentSearch(event: Event, query: string): void {
    event.stopPropagation();
    this.searchService.removeSearch(query);
    this.setViewState("search_performed", `Removed search from history: "${query}"`);
  }

  clearAllHistory(): void {
    this.searchService.clearHistory();
    this.setViewState("search_performed", "Cleared all search history");
  }

  private setViewState(
    eventAction: eventAction,
    eventDescription: string,
  ): void {
    this.viewContextHistorySvc.createContext({
      titleView: "Search Dialog",
      eventAction,
      eventDescription,
      thisState: {
        searchQuery: this.searchControl.value || "",
        resultCount: this.filteredResultsSubject.value.length,
        selectedResult: null,
        recentSearches: this.recentSearches,
      },
      route: this.route.snapshot,
      setAutomaticallyScreenshot: false,
    });
  }

}
